import type {
  AstNode,
  ChoiceOption,
  CompiledChoiceOption,
  CompiledScript,
  ForNode,
  IfNode,
  Instruction,
  InstForBegin,
  InstJumpIfFalse,
  InstSet,
  LValuePart,
  MacroNode,
  PixFile,
} from "./ast";
import { parseExpr, parseLValue } from "./expr";

/**
 * AST を実行可能なフラット命令列にコンパイル。
 */
export function compile(file: PixFile): CompiledScript {
  const compiler = new Compiler();
  compiler.run(file.nodes);
  return {
    filename: file.filename,
    instructions: compiler.instructions,
    labels: compiler.labels,
  };
}

class Compiler {
  instructions: Instruction[] = [];
  labels = new Map<string, number>();
  private pendingJumpLabels: Array<{ instIdx: number; targetLabel: string }> = [];
  private macros = new Map<string, AstNode[]>();

  run(nodes: AstNode[]): void {
    this.walk(nodes);
    // ラベルジャンプを解決
    for (const p of this.pendingJumpLabels) {
      const inst = this.instructions[p.instIdx]!;
      const target = this.labels.get(p.targetLabel);
      if (target === undefined) {
        // 別ファイル参照は実行時に解決(targetFile が入っているはず)
        if (
          (inst.type === "Jump" || inst.type === "Call") &&
          inst.targetFile !== undefined
        ) {
          continue;
        }
        // Choice の中身も同様
        if (inst.type === "Choice") continue;
        throw new Error(`未解決のラベル参照: ${p.targetLabel}`);
      }
      if (inst.type === "Jump" || inst.type === "Call") {
        inst.targetCmdIndex = target;
      }
    }
    // Choice の options 内ラベル解決
    for (const inst of this.instructions) {
      if (inst.type === "Choice") {
        for (const opt of inst.options) {
          if (opt.targetCmdIndex < 0 && opt.targetFile === undefined) {
            const t = this.labels.get(opt.targetLabel);
            if (t === undefined) {
              throw new Error(`未解決のラベル参照(choice): ${opt.targetLabel}`);
            }
            opt.targetCmdIndex = t;
          }
        }
      }
    }
  }

  private walk(nodes: AstNode[]): void {
    for (const node of nodes) {
      this.walkOne(node);
    }
  }

  private walkOne(node: AstNode): void {
    switch (node.type) {
      case "Label":
        this.labels.set(node.name, this.instructions.length);
        return;
      case "Speech":
        this.emit({
          type: "Say",
          speaker: node.speaker,
          segments: node.segments,
          line: node.line,
        });
        return;
      case "Narration":
        this.emit({
          type: "Say",
          speaker: null,
          segments: node.segments,
          line: node.line,
        });
        return;
      case "Command":
        this.compileCommand(node.name, node.subcommand, node.args, node.line);
        return;
      case "If":
        this.compileIf(node);
        return;
      case "For":
        this.compileFor(node);
        return;
      case "Macro":
        this.compileMacro(node);
        return;
      case "Choice":
        this.compileChoice(node.options, node.line);
        return;
    }
  }

  private emit(inst: Instruction): number {
    const idx = this.instructions.length;
    this.instructions.push(inst);
    return idx;
  }

  private compileCommand(
    name: string,
    subcommand: string | undefined,
    args: Record<string, unknown>,
    line: number,
  ): void {
    const raw = String(args["_raw"] ?? "");
    switch (name) {
      case "set": {
        const parsed = parseSetExpression(raw);
        const setInst: InstSet = {
          type: "Set",
          lvalue: parsed.lvalue,
          op: parsed.op,
          expr: parsed.expr,
          line,
        };
        this.emit(setInst);
        return;
      }
      case "unset": {
        const lvalue = parseLValue(raw.trim());
        this.emit({ type: "Unset", lvalue, line });
        return;
      }
      case "jump": {
        const target = parseTarget(raw.trim());
        const idx = this.emit({
          type: "Jump",
          targetFile: target.file,
          targetLabel: target.label,
          targetCmdIndex: -1,
          line,
        });
        this.pendingJumpLabels.push({ instIdx: idx, targetLabel: target.label });
        return;
      }
      case "call": {
        const target = parseTarget(raw.trim());
        const idx = this.emit({
          type: "Call",
          targetFile: target.file,
          targetLabel: target.label,
          targetCmdIndex: -1,
          line,
        });
        this.pendingJumpLabels.push({ instIdx: idx, targetLabel: target.label });
        return;
      }
      case "return":
        this.emit({ type: "Return", line });
        return;
      case "end":
        this.emit({ type: "End", line });
        return;
      case "wait": {
        const mode = pickWaitMode(args);
        this.emit({
          type: "Wait",
          mode: mode.mode,
          value: mode.value,
          channel: mode.channel,
          line,
        });
        return;
      }
      case "call_macro": {
        const macroName = String(args["name"] ?? raw.trim());
        const macro = this.macros.get(macroName);
        if (!macro) {
          // 未定義マクロ — ランタイム警告にせず無視
          return;
        }
        this.walk(macro);
        return;
      }
      default: {
        // プラグインコマンドとして emit
        const argsOnly: Record<string, string | number | boolean> = {};
        for (const [k, v] of Object.entries(args)) {
          if (k === "_raw") continue;
          argsOnly[k] = v as string | number | boolean;
        }
        this.emit({
          type: "Command",
          name,
          subcommand,
          args: argsOnly,
          line,
        });
        return;
      }
    }
  }

  private compileIf(node: IfNode): void {
    const endJumps: number[] = [];
    for (const branch of node.branches) {
      const condFalse: InstJumpIfFalse = {
        type: "JumpIfFalse",
        condition: branch.condition,
        targetCmdIndex: -1,
        line: node.line,
      };
      const condIdx = this.emit(condFalse);
      this.walk(branch.body);
      const endJ = this.emit({
        type: "Jump",
        targetLabel: "",
        targetCmdIndex: -1,
        line: node.line,
      });
      endJumps.push(endJ);
      condFalse.targetCmdIndex = this.instructions.length;
    }
    if (node.elseBody) {
      this.walk(node.elseBody);
    }
    const endIdx = this.instructions.length;
    for (const j of endJumps) {
      const inst = this.instructions[j]! as Extract<Instruction, { type: "Jump" }>;
      inst.targetCmdIndex = endIdx;
    }
  }

  private compileFor(node: ForNode): void {
    const step =
      node.step ?? ({ type: "Number" as const, value: 1 });
    const forBegin: InstForBegin = {
      type: "ForBegin",
      varName: node.varName,
      start: node.start,
      end: node.end,
      step,
      exitCmdIndex: -1,
      line: node.line,
    };
    const beginIdx = this.emit(forBegin);
    this.walk(node.body);
    this.emit({ type: "ForEnd", beginCmdIndex: beginIdx, line: node.line });
    forBegin.exitCmdIndex = this.instructions.length;
  }

  private compileMacro(node: MacroNode): void {
    this.macros.set(node.name, node.body);
  }

  private compileChoice(options: ChoiceOption[], line: number): void {
    const compiled: CompiledChoiceOption[] = options.map((opt) => ({
      text: opt.text,
      targetFile: opt.targetFile,
      targetLabel: opt.targetLabel,
      targetCmdIndex: -1,
      condition: opt.condition,
    }));
    this.emit({ type: "Choice", options: compiled, line });
  }
}

// =================================================================
// 補助関数
// =================================================================

function parseTarget(s: string): { file?: string; label: string } {
  const hashIdx = s.indexOf("#");
  if (hashIdx < 0) {
    return { label: s.trim() };
  }
  const file = s.slice(0, hashIdx).trim();
  const label = s.slice(hashIdx + 1).trim();
  return file === "" ? { label } : { file, label };
}

function parseSetExpression(raw: string): {
  lvalue: LValuePart[];
  op: "=" | "+=" | "-=" | "*=" | "/=";
  expr: ReturnType<typeof parseExpr>;
} {
  const split = splitAtAssignOp(raw);
  if (!split) {
    throw new Error(`@set の構文エラー: '${raw}'`);
  }
  const [lvalRaw, op, exprRaw] = split;
  return {
    lvalue: parseLValue(lvalRaw),
    op: op as "=" | "+=" | "-=" | "*=" | "/=",
    expr: parseExpr(exprRaw),
  };
}

function splitAtAssignOp(s: string): [string, string, string] | null {
  let depth = 0;
  let inStr = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (c === '"' && s[i - 1] !== "\\") inStr = !inStr;
    if (inStr) continue;
    if (c === "[") depth++;
    else if (c === "]") depth--;
    if (depth !== 0) continue;
    if (
      (c === "+" || c === "-" || c === "*" || c === "/") &&
      s[i + 1] === "="
    ) {
      return [s.slice(0, i).trim(), s.slice(i, i + 2), s.slice(i + 2).trim()];
    }
    if (c === "=" && s[i + 1] !== "=") {
      return [s.slice(0, i).trim(), "=", s.slice(i + 1).trim()];
    }
  }
  return null;
}

function pickWaitMode(args: Record<string, unknown>): {
  mode: "frame" | "time" | "key" | "bgm" | "voice";
  value?: number;
  channel?: number;
} {
  if (args["frame"] !== undefined) {
    return { mode: "frame", value: Number(args["frame"]) };
  }
  if (args["time"] !== undefined) {
    return { mode: "time", value: Number(args["time"]) };
  }
  if (args["key"] === true || args["key"] === "true") {
    return { mode: "key" };
  }
  if (args["bgm"] === true || args["bgm"] === "true") {
    return { mode: "bgm" };
  }
  if (args["voice"] !== undefined) {
    return { mode: "voice", channel: Number(args["voice"]) };
  }
  return { mode: "key" };
}
